import { Component, ReactNode } from "react";

export type Props = {
  /** Fallback UI (what to render if an error occurs) */
  fallback: ReactNode;
  /** Normal content (what to try rendering) */
  children: ReactNode;
  /** Optional: Reset condition to clear error state */
  resetKey?: unknown;
  /** Optional: Callback when error is caught */
  onError?: (error: unknown, errorInfo?: unknown) => void;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Additional CSS classes for outer container */
  className?: string;
};

export type State = {
  hasError: boolean;
};

/**
 * Base error boundary component with no styling.
 * Catches JavaScript errors in child components and renders a fallback UI.
 */
export class ErrorBoundaryBase extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo?: unknown) {
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    const { "data-testid": testId = "error-boundary-base", className } =
      this.props;

    if (this.state.hasError) {
      return (
        <div data-testid={`${testId}-fallback`} className={className}>
          {this.props.fallback}
        </div>
      );
    }
    return (
      <div data-testid={testId} className={className}>
        {this.props.children}
      </div>
    );
  }
}

export default ErrorBoundaryBase;
